/**
 * Quick View JavaScript
 * Handles product quick view modal functionality
 */

class QuickView {
  constructor() {
    this.modal = document.getElementById('quick-view-modal');
    this.modalBody = this.modal?.querySelector('[data-quick-view-body]');
    this.closeButtons = document.querySelectorAll('[data-quick-view-close]');
    this.quickViewButtons = document.querySelectorAll('[data-quick-view]');
    
    if (!this.modal) return;
    
    this.init();
  }

  init() {
    // Close modal handlers
    this.closeButtons.forEach(button => {
      button.addEventListener('click', () => this.close());
    });

    // Quick view button handlers
    this.quickViewButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const handle = button.dataset.productHandle;
        this.open(handle);
      });
    });

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
  }

  isOpen() {
    return this.modal?.classList.contains('quick-view-modal--open');
  }

  async open(handle) {
    if (!this.modal || !handle) return;
    
    // Show loading state
    this.modal.classList.add('quick-view-modal--open');
    this.modalBody.innerHTML = this.getLoadingHTML();
    document.body.style.overflow = 'hidden';
    
    // Load product
    try {
      const product = await this.fetchProduct(handle);
      this.modalBody.innerHTML = this.renderProduct(product);
      this.initProductHandlers(product);
    } catch (error) {
      console.error('Error loading product:', error);
      this.modalBody.innerHTML = this.getErrorHTML();
    }
  }

  initProductHandlers(product) {
    // Image slider
    this.initSlider();
    
    // Variant selection
    this.initVariants(product);
    
    // Quantity selector
    this.initQuantity();
    
    // Form submission
    this.initForm();
    
    // Wishlist button
    const wishlistButton = this.modalBody.querySelector('[data-wishlist-toggle]');
    if (wishlistButton && window.wishlist) {
      window.wishlist.updateWishlistButton(wishlistButton);
    }
  }

  initSlider() {
    const gallery = this.modalBody.querySelector('[data-quick-view-gallery]');
    if (!gallery) return;
    
    const slides = gallery.querySelectorAll('.quick-view-product__slide');
    const thumbnails = gallery.querySelectorAll('.quick-view-product__thumbnail');
    const thumbnailsContainer = gallery.querySelector('[data-thumbnails-container]');
    const prevBtn = gallery.querySelector('[data-thumb-nav="prev"]');
    const nextBtn = gallery.querySelector('[data-thumb-nav="next"]');
    let currentSlide = 0;
    
    const showSlide = (index) => {
      // Remove active classes
      slides.forEach((slide, i) => {
        slide.classList.remove('quick-view-product__slide--active');
        if (i === index) {
          slide.style.transform = 'translateX(0)';
        } else if (i < index) {
          slide.style.transform = 'translateX(-100%)';
        } else {
          slide.style.transform = 'translateX(100%)';
        }
      });
      
      // Add active class with transition
      slides.forEach((slide, i) => {
        if (i === index) {
          setTimeout(() => {
            slide.classList.add('quick-view-product__slide--active');
          }, 10);
        }
      });
      
      thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle('quick-view-product__thumbnail--active', i === index);
      });
      
      // Scroll active thumbnail into view
      if (thumbnails[index] && thumbnailsContainer) {
        const thumb = thumbnails[index];
        const containerRect = thumbnailsContainer.getBoundingClientRect();
        const thumbRect = thumb.getBoundingClientRect();
        
        if (thumbRect.left < containerRect.left) {
          thumbnailsContainer.scrollLeft -= (containerRect.left - thumbRect.left + 10);
        } else if (thumbRect.right > containerRect.right) {
          thumbnailsContainer.scrollLeft += (thumbRect.right - containerRect.right + 10);
        }
      }
      
      currentSlide = index;
    };
    
    thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => showSlide(index));
    });
    
    // Thumbnail navigation arrows
    if (prevBtn && thumbnailsContainer) {
      prevBtn.addEventListener('click', () => {
        thumbnailsContainer.scrollBy({ left: -100, behavior: 'smooth' });
      });
    }
    
    if (nextBtn && thumbnailsContainer) {
      nextBtn.addEventListener('click', () => {
        thumbnailsContainer.scrollBy({ left: 100, behavior: 'smooth' });
      });
    }
  }

  initVariants(product) {
    const variantButtons = this.modalBody.querySelectorAll('.quick-view-product__variant-button');
    const variantSelects = this.modalBody.querySelectorAll('[data-option-select]');
    const variantIdInput = this.modalBody.querySelector('#quick-view-variant-id');
    
    if (!variantIdInput) return;
    
    const updateVariant = () => {
      const selectedOptions = {};
      variantSelects.forEach(select => {
        if (select.value) {
          selectedOptions[select.dataset.optionSelect] = select.value;
        }
      });
      
      const variant = product.variants.find(v => {
        return v.options.every((option, index) => {
          return option === selectedOptions[String(index + 1)];
        });
      });
      
      if (variant) {
        variantIdInput.value = variant.id;
        
        // Update price
        const priceEl = this.modalBody.querySelector('.quick-view-product__price');
        if (priceEl) {
          const price = variant.compare_at_price > variant.price
            ? `<span class="quick-view-product__price-compare">${this.formatMoney(variant.compare_at_price)}</span> <span class="quick-view-product__price-regular">${this.formatMoney(variant.price)}</span>`
            : `<span class="quick-view-product__price-regular">${this.formatMoney(variant.price)}</span>`;
          priceEl.innerHTML = price;
        }
        
        // Update stock status
        const stockEl = this.modalBody.querySelector('.quick-view-product__stock');
        const stockHTML = variant.available
          ? `<div class="quick-view-product__stock"><span>${this.modal.dataset.inStock || ''}</span></div>`
          : `<div class="quick-view-product__stock quick-view-product__stock--out"><span>${(window.themeStrings && window.themeStrings.soldOut) || ''}</span></div>`;
        if (stockEl) {
          stockEl.outerHTML = stockHTML;
        }
        
        // Update buttons
        const buttons = this.modalBody.querySelectorAll('.quick-view-product__button');
        buttons.forEach(btn => {
          btn.disabled = !variant.available;
        });
      }
    };
    
    variantButtons.forEach(button => {
      button.addEventListener('click', () => {
        const group = button.closest('.quick-view-product__variant-group');
        const option = button.dataset.option;
        const value = button.dataset.value;
        
        // Update button states
        group.querySelectorAll('.quick-view-product__variant-button').forEach(b => {
          b.classList.remove('quick-view-product__variant-button--active');
        });
        button.classList.add('quick-view-product__variant-button--active');
        
        // Update select
        const select = group.querySelector(`[data-option-select="${option}"]`);
        if (select) {
          select.value = value;
          updateVariant();
        }
      });
    });
    
    variantSelects.forEach(select => {
      select.addEventListener('change', () => updateVariant());
    });
  }

  initQuantity() {
    const quantityInput = this.modalBody.querySelector('#quick-view-quantity');
    const minusBtn = this.modalBody.querySelector('.quick-view-product__quantity-btn--minus');
    const plusBtn = this.modalBody.querySelector('.quick-view-product__quantity-btn--plus');
    
    if (minusBtn && quantityInput) {
      minusBtn.addEventListener('click', () => {
        const currentValue = parseInt(quantityInput.value) || 1;
        if (currentValue > 1) {
          quantityInput.value = currentValue - 1;
        }
      });
    }
    
    if (plusBtn && quantityInput) {
      plusBtn.addEventListener('click', () => {
        const currentValue = parseInt(quantityInput.value) || 1;
        quantityInput.value = currentValue + 1;
      });
    }
    
    if (quantityInput) {
      quantityInput.addEventListener('change', function() {
        const value = parseInt(this.value) || 1;
        if (value < 1) {
          this.value = 1;
        }
      });
    }
  }

  initForm() {
    const form = this.modalBody.querySelector('#quick-view-form');
    if (!form) return;
    
    // Add to cart
    const addToCartBtn = form.querySelector('.quick-view-product__button--add-to-cart');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const formData = new FormData(form);
        
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });
          
          if (response.ok) {
            const event = new CustomEvent('cart:updated');
            document.dispatchEvent(event);
            
            // Show success message
            addToCartBtn.textContent = (window.themeStrings && window.themeStrings.added) || '';
            addToCartBtn.disabled = true;
            
            setTimeout(() => {
              addToCartBtn.textContent = (window.themeStrings && window.themeStrings.addToCart) || '';
              addToCartBtn.disabled = false;
            }, 2000);
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
        }
      });
    }
    
    // Buy now
    const buyNowBtn = form.querySelector('.quick-view-product__button--buy-now');
    if (buyNowBtn) {
      buyNowBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const formData = new FormData(form);
        formData.append('return_to', '/checkout');
        
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });
          
          if (response.ok) {
            window.location.href = '/checkout';
          } else {
            window.alert((window.themeStrings && window.themeStrings.addError) || '');
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
        }
      });
    }
  }

  close() {
    if (!this.modal) return;
    this.modal.classList.remove('quick-view-modal--open');
    document.body.style.overflow = '';
  }

  async fetchProduct(handle) {
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) throw new Error('Product not found');
    return await response.json();
  }

  renderProduct(product) {
    const selectedVariant = product.variants.find(v => v.available) || product.variants[0];
    const price = selectedVariant.compare_at_price > selectedVariant.price
      ? `<span class="quick-view-product__price-compare">${this.formatMoney(selectedVariant.compare_at_price)}</span> <span class="quick-view-product__price-regular">${this.formatMoney(selectedVariant.price)}</span>`
      : `<span class="quick-view-product__price-regular">${this.formatMoney(selectedVariant.price)}</span>`;

    // Collection name - get from first available variant's collection or product URL
    const collectionName = product.collections && product.collections.length > 0
      ? `<div class="quick-view-product__collection">${product.collections[0]}</div>`
      : product.type 
        ? `<div class="quick-view-product__collection">${product.type}</div>`
        : '';

    // Images slider - Shopify returns images as array of strings
    let images = [];
    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      images = product.images;
    } else if (product.featured_image) {
      images = [product.featured_image];
    } else {
      images = [];
    }
    
    const imagesHTML = images.length > 0
      ? images.map((img, index) => `
          <div class="quick-view-product__slide ${index === 0 ? 'quick-view-product__slide--active' : ''}">
            <img 
              src="${this.getImageSrc(img, '800')}" 
              alt="${product.title}" 
              loading="${index === 0 ? 'eager' : 'lazy'}"
              data-slide-index="${index}"
            >
          </div>
        `).join('')
      : `<div class="quick-view-product__slide quick-view-product__slide--active">
        <img 
            src="${this.getImageSrc(product.featured_image || '', '800')}" 
          alt="${product.title}" 
          loading="eager"
            data-slide-index="0"
          >
        </div>`;

    // Thumbnails - show max 5 at a time, scrollable with arrows
    const thumbnailsHTML = images.length > 1
      ? images.map((img, index) => `
          <button 
            type="button" 
            class="quick-view-product__thumbnail ${index === 0 ? 'quick-view-product__thumbnail--active' : ''}" 
            data-slide-index="${index}"
            aria-label="Go to slide ${index + 1}"
          >
            <img 
              src="${this.getImageSrc(img, '100')}" 
              alt="${product.title} - Image ${index + 1}"
              loading="lazy"
            >
          </button>
        `).join('')
      : '';

    // Variants
    const hasVariants = product.variants && product.variants.length > 1;
    const variantOptions = this.getVariantOptions(product);
    const variantsHTML = hasVariants && variantOptions.length > 0
      ? variantOptions.map(option => {
          // getVariantOptions already returns name as string, but ensure it's safe
          let optionName = `Option ${option.position || ''}`;
          
          if (option.name) {
            if (typeof option.name === 'string' && option.name.trim() !== '' && !option.name.includes('[object')) {
              optionName = option.name.trim();
            } else if (typeof option.name === 'object' && option.name !== null) {
              // Try to extract from object
              const extractedName = option.name.name || option.name.title || option.name.label || option.name.value;
              if (extractedName && typeof extractedName === 'string' && extractedName.trim() !== '' && !extractedName.includes('[object')) {
                optionName = extractedName.trim();
              } else if (extractedName && typeof extractedName !== 'object') {
                const strName = String(extractedName).trim();
                if (strName !== '' && strName !== '[object Object]' && !strName.includes('[object')) {
                  optionName = strName;
                }
              }
            } else {
              const strName = String(option.name).trim();
              if (strName !== '' && strName !== '[object Object]' && !strName.includes('[object')) {
                optionName = strName;
              }
            }
          }
          
          // Final fallback check
          if (!optionName || optionName === '' || optionName === '[object Object]' || optionName.includes('[object')) {
            optionName = `Option ${option.position || ''}`;
          }
          
          // All variants (including Color) now use text buttons like product page
          return `
            <div class="quick-view-product__variant-group">
              <label class="quick-view-product__variant-label">${optionName}:</label>
              <div class="quick-view-product__variant-buttons">
                ${option.values.map(value => `
                  <button
                    type="button"
                    class="quick-view-product__variant-button ${option.selected_value === value ? 'quick-view-product__variant-button--active' : ''}"
                    data-option="${option.position}"
                    data-value="${value}"
                    aria-label="${optionName}: ${value}"
                  >
                    ${value}
                  </button>
                `).join('')}
              </div>
              <select name="options[${optionName}]" class="visually-hidden" data-option-select="${option.position}">
                ${option.values.map(value => `
                  <option value="${value}" ${option.selected_value === value ? 'selected' : ''}>${value}</option>
                `).join('')}
              </select>
            </div>
          `;
        }).join('')
      : '';

    // Stock status
    const inStockLabel = this.modal?.dataset.inStock || '';
    const soldOutLabel = (window.themeStrings && window.themeStrings.soldOut) || '';
    const stockHTML = selectedVariant.available
      ? `<div class="quick-view-product__stock"><span>${inStockLabel}</span></div>`
      : `<div class="quick-view-product__stock quick-view-product__stock--out"><span>${soldOutLabel}</span></div>`;

    return `
      <div class="quick-view-product__gallery" data-quick-view-gallery>
        <div class="quick-view-product__slider">
          ${imagesHTML}
        </div>
        ${images.length > 1 ? `
          <div class="quick-view-product__thumbnails-wrapper">
            <button type="button" class="quick-view-product__thumbnail-nav quick-view-product__thumbnail-nav--prev" data-thumb-nav="prev" aria-label="Previous thumbnails">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4L6 10L12 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <div class="quick-view-product__thumbnails" data-thumbnails-container>
              ${thumbnailsHTML}
            </div>
            <button type="button" class="quick-view-product__thumbnail-nav quick-view-product__thumbnail-nav--next" data-thumb-nav="next" aria-label="Next thumbnails">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 4L14 10L8 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        ` : ''}
      </div>
      <div class="quick-view-product__details">
        ${collectionName}
        <h2 class="quick-view-product__title">${product.title}</h2>
        <div class="quick-view-product__price-wrapper">
          <div class="quick-view-product__price">${price}</div>
          <button type="button" class="quick-view-product__wishlist" data-wishlist-toggle data-product-id="${product.id}" data-product-handle="${product.handle}" aria-label="${(window.themeStrings && window.themeStrings.addWishlist) || ''}">
            <svg class="quick-view-product__wishlist-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.366 3.841a4.908 4.908 0 00-6.94 0L10 4.267l-.425-.426a4.908 4.908 0 00-6.941 6.94l.426.425L10 18.233l7.366-7.366.425-.425a4.908 4.908 0 000-6.94v0z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <form class="quick-view-product__form" id="quick-view-form">
          ${hasVariants ? `<div class="quick-view-product__variants">${variantsHTML}</div>` : ''}
          <input type="hidden" name="id" id="quick-view-variant-id" value="${selectedVariant.id}">
          ${stockHTML}
          <div class="quick-view-product__quantity-actions">
            <div class="quick-view-product__quantity-selector">
              <button type="button" class="quick-view-product__quantity-btn quick-view-product__quantity-btn--minus" aria-label="Decrease quantity">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 8H12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
              <input type="number" id="quick-view-quantity" name="quantity" value="1" min="1" class="quick-view-product__quantity-input">
              <button type="button" class="quick-view-product__quantity-btn quick-view-product__quantity-btn--plus" aria-label="Increase quantity">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 4V12M4 8H12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            <button type="submit" name="add" class="quick-view-product__button quick-view-product__button--add-to-cart" ${!selectedVariant.available ? 'disabled' : ''}>
              ${(window.themeStrings && window.themeStrings.addToCart) || ''}
            </button>
          </div>
          <button type="submit" name="add" class="quick-view-product__button quick-view-product__button--buy-now" data-buy-now ${!selectedVariant.available ? 'disabled' : ''}>
            ${(window.themeStrings && window.themeStrings.buyNow) || ''}
          </button>
          <a href="${product.url}" class="quick-view-product__view-details">
            <span>${(window.themeStrings && window.themeStrings.viewDetails) || ''}</span>
          </a>
        </form>
      </div>
    `;
  }

  getVariantOptions(product) {
    if (!product.variants || product.variants.length === 0) return [];
    
    // Shopify product JSON includes options_with_values array - use it directly like Liquid does
    if (product.options_with_values && Array.isArray(product.options_with_values) && product.options_with_values.length > 0) {
      return product.options_with_values.map((option, index) => {
        // Get selected value - Shopify JSON includes selected_value
        let selectedValue = option.selected_value || '';
        
        // If no selected_value, get from first variant
        if (!selectedValue) {
          const firstVariant = product.variants && product.variants.length > 0 ? product.variants[0] : null;
          if (firstVariant && firstVariant.options && firstVariant.options[index] !== undefined) {
            selectedValue = firstVariant.options[index];
          } else if (option.values && option.values.length > 0) {
            selectedValue = option.values[0];
          }
        }
        
        // Safely extract option name - handle different formats
        // Shopify JSON API returns option.name as string, but sometimes it can be an object
        // First priority: use product.options array (most reliable)
        let optionName = `Option ${option.position || (index + 1)}`;
        
        // First try product.options array (most reliable source)
        if (product.options && Array.isArray(product.options) && product.options[index] !== undefined) {
          const productOption = product.options[index];
          if (typeof productOption === 'string' && productOption.trim() !== '') {
            optionName = productOption.trim();
          } else if (typeof productOption === 'object' && productOption !== null) {
            // Try to extract name from object
            const extractedName = productOption.name || productOption.title || productOption.label || productOption.value;
            if (extractedName && typeof extractedName === 'string' && extractedName.trim() !== '') {
              optionName = extractedName.trim();
            } else if (extractedName && typeof extractedName !== 'object') {
              optionName = String(extractedName).trim();
            }
          } else if (productOption !== null && productOption !== undefined) {
            const strName = String(productOption).trim();
            if (strName !== '' && strName !== '[object Object]') {
              optionName = strName;
            }
          }
        }
        
        // Second priority: try option.name (if product.options didn't work)
        if (optionName.startsWith('Option ') && option.name !== undefined && option.name !== null) {
          if (typeof option.name === 'string' && option.name.trim() !== '') {
            optionName = option.name.trim();
          } else if (typeof option.name === 'object') {
            // If it's an object, try common properties
            const extractedName = option.name.name || option.name.title || option.name.label || option.name.value;
            if (extractedName && typeof extractedName === 'string' && extractedName.trim() !== '') {
              optionName = extractedName.trim();
            } else if (extractedName && typeof extractedName !== 'object') {
              optionName = String(extractedName).trim();
            }
          } else {
            const strName = String(option.name).trim();
            if (strName !== '' && strName !== '[object Object]') {
              optionName = strName;
            }
          }
        }
        
        // Final validation - ensure we always return a valid string
        if (!optionName || optionName === '' || optionName === '[object Object]' || optionName.includes('[object')) {
          optionName = `Option ${option.position || (index + 1)}`;
        }
        
        return {
          position: option.position || (index + 1),
          name: String(optionName), // Explicitly convert to string
          values: (option.values || []).map(v => String(v)),
          selected_value: String(selectedValue)
        };
      });
    }
    
    // Fallback: build from variants (old method)
    const options = {};
    product.variants.forEach(variant => {
      variant.options.forEach((value, index) => {
        const optionPosition = index + 1;
        if (!options[optionPosition]) {
          // Use product.options array if available
          let optionName = `Option ${optionPosition}`;
          
          if (product.options && Array.isArray(product.options) && product.options[index] !== undefined) {
            const productOption = product.options[index];
            if (typeof productOption === 'string' && productOption.trim() !== '') {
              optionName = productOption.trim();
            } else if (typeof productOption === 'object' && productOption !== null) {
              const extractedName = productOption.name || productOption.title || productOption.label || productOption.value;
              if (extractedName && typeof extractedName === 'string' && extractedName.trim() !== '' && !extractedName.includes('[object')) {
                optionName = extractedName.trim();
              } else if (extractedName && typeof extractedName !== 'object') {
                const strName = String(extractedName).trim();
                if (strName !== '' && strName !== '[object Object]' && !strName.includes('[object')) {
                  optionName = strName;
                }
              }
            } else if (productOption !== null && productOption !== undefined) {
              const strName = String(productOption).trim();
              if (strName !== '' && strName !== '[object Object]' && !strName.includes('[object')) {
                optionName = strName;
              }
            }
          }
          
          // Final validation
          if (!optionName || optionName === '' || optionName === '[object Object]' || optionName.includes('[object')) {
            optionName = `Option ${optionPosition}`;
          }
          
          options[optionPosition] = {
            position: optionPosition,
            name: String(optionName),
            values: [],
            selected_value: value
          };
        }
        if (!options[optionPosition].values.includes(value)) {
          options[optionPosition].values.push(String(value));
        }
      });
    });
    
    return Object.values(options);
  }

  getColorValue(colorName) {
    const colorMap = {
      'black': '#000000',
      'white': '#FFFFFF',
      'red': '#FF0000',
      'blue': '#0000FF',
      'green': '#008000',
      'yellow': '#FFFF00',
      'pink': '#FFC0CB',
      'purple': '#800080',
      'orange': '#FFA500',
      'brown': '#A52A2A',
      'grey': '#808080',
      'gray': '#808080',
      'cream': '#FFFDD0',
      'beige': '#F5F5DC',
      'navy': '#000080',
      'tan': '#D2B48C',
      'linen': '#FAF0E6'
    };
    
    const normalized = colorName.toLowerCase().trim().replace(/\s+/g, '');
    return colorMap[normalized] || `#${Math.floor(Math.random()*16777215).toString(16)}`;
  }

  getLoadingHTML() {
    return `
      <div style="text-align: center; padding: 4rem;">
        <div class="loading-spinner"></div>
        <p style="margin-top: 1rem; color: var(--color-text); opacity: 0.6;">Loading...</p>
      </div>
    `;
  }

  getErrorHTML() {
    return `
      <div style="text-align: center; padding: 4rem;">
        <p style="color: var(--color-text); opacity: 0.6;">Unable to load product. Please try again.</p>
      </div>
    `;
  }

  getImageSrc(image, size = '600') {
    if (!image) return `https://placehold.co/${size}x${size}/F5F5F0/8B8B83`;
    
    let imgSrc = '';
    
    // If image is an object with src property
    if (typeof image === 'object' && image !== null) {
      if (image.src) {
        imgSrc = String(image.src);
      } else if (image.url) {
        imgSrc = String(image.url);
      } else {
        imgSrc = String(image);
      }
    } else {
      // If image is a string
      imgSrc = typeof image === 'string' ? image : String(image);
    }
    
    // Ensure protocol - handle //cdn.shopify.com format
    if (imgSrc.startsWith('//')) {
      imgSrc = 'https:' + imgSrc;
    } else if (!imgSrc.includes('http')) {
      imgSrc = `https:${imgSrc}`;
    }
    
    // Try to resize if it's a Shopify CDN URL
    // Shopify URLs format: .../files/1/.../image.jpg?width=XXX or .../image.jpg
    if (imgSrc.includes('cdn.shopify.com') || imgSrc.includes('shopifycdn.com')) {
      try {
        // Try to add width parameter or replace existing
        const urlObj = new URL(imgSrc);
        urlObj.searchParams.set('width', size);
        imgSrc = urlObj.toString();
      } catch (e) {
        // If URL parsing fails, try manual approach
        if (imgSrc.includes('?')) {
          imgSrc = imgSrc.split('?')[0] + `?width=${size}`;
        } else {
          imgSrc = imgSrc + `?width=${size}`;
        }
      }
    }
    
    return imgSrc;
  }

  formatMoney(cents) {
    if (window.themeFormatMoney) return window.themeFormatMoney(cents);
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.themeCurrency || 'USD'
    }).format(cents / 100);
  }

  truncate(str, maxLength) {
    if (str.length <= maxLength) return str;
    return str.slice(0, maxLength) + '...';
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new QuickView());
} else {
  new QuickView();
}

